import { useEffect, useState } from "react";
import { Plus, Edit, Trash2, Eye, EyeOff } from "lucide-react";

import AdminLayout from "@/pages/admin/AdminLayout";
import { adminListBlog, adminUpsertBlog, adminDeleteBlog } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import RichTextEditor from "@/components/RichTextEditor";

const EMPTY = { slug: "", title: "", excerpt: "", body_html: "", published: false, author: "CheckExpert", image_url: "" };

export default function AdminBlog() {
  const { toast } = useToast();
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  function load() {
    adminListBlog().then((d) => setPosts(d.posts || []));
  }
  useEffect(load, []);

  function setField(k, v) {
    setForm((s) => ({ ...s, [k]: v }));
  }

  function startNew() {
    setEditing("new");
    setForm(EMPTY);
  }

  function startEdit(p) {
    setEditing(p.slug);
    setForm({ ...EMPTY, ...p });
  }

  async function save() {
    try {
      await adminUpsertBlog(form, editing === "new" ? null : editing);
      toast({ title: "Opgeslagen" });
      setEditing(null);
      load();
    } catch (err) {
      toast({ title: "Fout", description: err?.response?.data?.detail || "Probeer opnieuw", variant: "destructive" });
    }
  }

  async function remove(slug) {
    if (!window.confirm("Verwijder deze post?")) return;
    await adminDeleteBlog(slug);
    toast({ title: "Verwijderd" });
    load();
  }

  return (
    <AdminLayout>
      <div data-testid="admin-blog-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-display text-3xl font-bold text-ink tracking-tight">Blog</h1>
            <p className="text-ink-soft mt-1 text-sm">{posts.length} posts</p>
          </div>
          {!editing && (
            <button onClick={startNew} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#002FA7] text-white text-sm font-semibold" data-testid="new-post">
              <Plus className="h-4 w-4" /> Nieuwe post
            </button>
          )}
        </div>

        {editing ? (
          <div className="mt-6 rounded-2xl bg-white border border-border p-6 space-y-4">
            <h2 className="font-display text-xl font-semibold text-ink">{editing === "new" ? "Nieuwe post" : `Bewerk: ${editing}`}</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="slug">Slug</Label>
                <Input id="slug" value={form.slug} onChange={(e) => setField("slug", e.target.value)} disabled={editing !== "new"} />
              </div>
              <div>
                <Label htmlFor="title">Titel</Label>
                <Input id="title" value={form.title} onChange={(e) => setField("title", e.target.value)} />
              </div>
            </div>
            <div>
              <Label htmlFor="excerpt">Excerpt</Label>
              <Textarea id="excerpt" rows={2} value={form.excerpt} onChange={(e) => setField("excerpt", e.target.value)} />
            </div>
            <div>
              <Label htmlFor="image_url">Image URL</Label>
              <Input id="image_url" value={form.image_url || ""} onChange={(e) => setField("image_url", e.target.value)} />
            </div>
            <div>
              <Label htmlFor="body_html">Body (rich text)</Label>
              <RichTextEditor value={form.body_html} onChange={(html) => setField("body_html", html)} />
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="published" checked={form.published} onChange={(e) => setField("published", e.target.checked)} />
              <Label htmlFor="published">Gepubliceerd</Label>
            </div>
            <div className="flex gap-2 pt-2">
              <button onClick={save} className="px-5 py-2.5 rounded-full bg-[#002FA7] text-white text-sm font-semibold">Opslaan</button>
              <button onClick={() => setEditing(null)} className="px-5 py-2.5 rounded-full bg-white border border-border text-ink text-sm font-semibold">Annuleren</button>
            </div>
          </div>
        ) : (
          <div className="mt-6 space-y-3">
            {posts.length === 0 ? (
              <p className="text-ink-soft">Nog geen blog-posts.</p>
            ) : (
              posts.map((p) => (
                <div key={p.slug} className="rounded-2xl bg-white border border-border p-5 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-display font-semibold text-ink truncate">{p.title}</h3>
                      {p.published ? <Eye className="h-4 w-4 text-emerald-600" /> : <EyeOff className="h-4 w-4 text-amber-600" />}
                    </div>
                    <p className="text-xs text-ink-subtle mt-1">{p.slug} · {p.published_at?.slice(0, 10) || "draft"}</p>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button onClick={() => startEdit(p)} className="px-3 py-1.5 rounded-lg bg-slate-100 text-ink text-xs font-semibold inline-flex items-center gap-1">
                      <Edit className="h-3.5 w-3.5" /> Bewerk
                    </button>
                    <button onClick={() => remove(p.slug)} className="px-3 py-1.5 rounded-lg bg-red-600 text-white text-xs font-semibold inline-flex items-center gap-1">
                      <Trash2 className="h-3.5 w-3.5" /> Wis
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
